import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  UserPlus,
  Search,
  Edit2,
  Trash2,
  X,
  Save,
  Eye,
  EyeOff,
  Users,
  Mail,
  Lock,
  Shield,
  CheckCircle,
  XCircle,
  UserCheck,
  UserX,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Upload,
  Phone,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import Input from '../components/ui/input';
import Label from '../components/ui/label';
import {
  getUsers,
  getUser,
  createUser,
  updateUser,
  deleteUser,
  bulkCreateUsers,
  getTenantTicketAccessOptions,
} from '../api/users';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const UserManagement = () => {
  const USERS_PAGE_SIZE = 10;
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'customer',
    is_active: true,
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkUsers, setBulkUsers] = useState([]);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [tenantAccessOptions, setTenantAccessOptions] = useState([]);
  const [ticketAccessTenantIds, setTicketAccessTenantIds] = useState([]);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    const open = showCreateModal || showEditModal;
    const canLoad =
      open && (currentUser?.role === 'admin' || currentUser?.role === 'super_admin');
    if (!canLoad) return;
    (async () => {
      try {
        const res = await getTenantTicketAccessOptions();
        if (res?.success) setTenantAccessOptions(res.data || []);
      } catch (e) {
        console.error('Tenant options load error:', e);
      }
    })();
  }, [showCreateModal, showEditModal, currentUser?.role]);

  useEffect(() => {
    filterUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, searchTerm, roleFilter, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, statusFilter, sortBy, sortOrder]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await getUsers();
      if (response.success) {
        setUsers(response.data || []);
      } else {
        toast.error('Failed to load users');
      }
    } catch (error) {
      console.error('Load users error:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics – case-insensitive role, normalize is_active (0/1/true/false)
  const statistics = useMemo(() => {
    const total = users.length;
    const isActive = (u) => u.is_active === true || u.is_active === 1 || String(u.is_active) === '1';
    const roleLower = (u) => (u.role || '').toString().toLowerCase();
    const active = users.filter(u => isActive(u)).length;
    const inactive = users.filter(u => !isActive(u)).length;
    const admins = users.filter(u => roleLower(u) === 'admin').length;
    const customers = users.filter(u => roleLower(u) === 'customer').length;

    return { total, active, inactive, admins, customers };
  }, [users]);

  /** Click a stat card to apply matching table filters (e.g. Admins → role = admin). */
  const applyStatCardFilter = (card) => {
    switch (card) {
      case 'total':
        setRoleFilter('all');
        setStatusFilter('all');
        break;
      case 'active':
        setRoleFilter('all');
        setStatusFilter('active');
        break;
      case 'inactive':
        setRoleFilter('all');
        setStatusFilter('inactive');
        break;
      case 'admin':
        setRoleFilter('admin');
        setStatusFilter('all');
        break;
      case 'customer':
        setRoleFilter('customer');
        setStatusFilter('all');
        break;
      default:
        break;
    }
  };

  const statCardActive = (card) => {
    switch (card) {
      case 'total':
        return roleFilter === 'all' && statusFilter === 'all';
      case 'active':
        return roleFilter === 'all' && statusFilter === 'active';
      case 'inactive':
        return roleFilter === 'all' && statusFilter === 'inactive';
      case 'admin':
        return roleFilter === 'admin' && statusFilter === 'all';
      case 'customer':
        return roleFilter === 'customer' && statusFilter === 'all';
      default:
        return false;
    }
  };

  const statCardClass = (base, card) =>
    `${base} cursor-pointer transition-all duration-300 ${
      statCardActive(card)
        ? 'ring-2 ring-indigo-500 dark:ring-indigo-400 ring-offset-2 ring-offset-white dark:ring-offset-slate-900 shadow-lg scale-[1.02]'
        : 'hover:shadow-lg hover:-translate-y-1'
    }`;

  const filterUsers = () => {
    let filtered = [...users];

    // Search filter – safe for null/undefined name/email/employee code
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter(
        (user) =>
          (String(user.name || '').toLowerCase().includes(term)) ||
          (String(user.email || '').toLowerCase().includes(term)) ||
          (String(user.employee_code || user.employee_id || '').toLowerCase().includes(term))
      );
    }

    // Role filter – case-insensitive (API may return Admin, admin, etc.)
    if (roleFilter !== 'all') {
      const roleLower = roleFilter.toLowerCase();
      filtered = filtered.filter(
        (user) => (String(user.role || '').toLowerCase() === roleLower)
      );
    }

    // Status filter – handle 0/1, true/false, or string from API
    if (statusFilter !== 'all') {
      filtered = filtered.filter((user) => {
        const isActive = user.is_active === true || user.is_active === 1 || String(user.is_active) === '1';
        return statusFilter === 'active' ? isActive : !isActive;
      });
    }

    // Sorting – safe for null/undefined and is_active 0/1
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = (a.name || '').toString().toLowerCase();
          bValue = (b.name || '').toString().toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toString().toLowerCase();
          bValue = (b.email || '').toString().toLowerCase();
          break;
        case 'role':
          aValue = (a.role || '').toString().toLowerCase();
          bValue = (b.role || '').toString().toLowerCase();
          break;
        case 'status':
          aValue = (a.is_active === true || a.is_active === 1 || String(a.is_active) === '1') ? 1 : 0;
          bValue = (b.is_active === true || b.is_active === 1 || String(b.is_active) === '1') ? 1 : 0;
          break;
        case 'created':
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredUsers(filtered);
  };

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedUsers = filteredUsers.slice(
    (safeCurrentPage - 1) * USERS_PAGE_SIZE,
    safeCurrentPage * USERS_PAGE_SIZE
  );

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const exportToCSV = () => {
    const headers = ['Employee Code', 'Name', 'Email', 'Role', 'Status', 'Created Date'];
    const rows = filteredUsers.map(user => [
      user.employee_code || user.employee_id || '',
      user.name,
      user.email,
      user.role,
      isUserActive(user) ? 'Active' : 'Inactive',
      formatDate(user.created_at)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `users_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Users exported to CSV');
  };

  const downloadBulkTemplate = () => {
    const templateRows = [
      'employee_id,name,email,role',
      'EMP001,John Doe,john.doe@company.com,employee',
      'EMP002,Jane Smith,jane.smith@company.com,customer'
    ];
    const blob = new Blob([templateRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'user_bulk_upload_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const parseCsvLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values.map((v) => v.replace(/^"(.*)"$/, '$1').trim());
  };

  const handleBulkFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBulkFileName(file.name);
    setBulkErrors([]);
    try {
      const text = await file.text();
      const lines = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
      if (lines.length < 2) {
        setBulkUsers([]);
        setBulkErrors(['File is empty or has no data rows']);
        return;
      }

      const normalizeHeader = (h) => {
        const key = String(h || '').trim().toLowerCase().replace(/\s+/g, '_');
        if (key === 'emp_id' || key === 'employee_code') return 'employee_id';
        if (key === 'e-mail') return 'email';
        return key;
      };
      const normalizeRole = (raw) => {
        const role = String(raw || '').trim().toLowerCase();
        if (role === 'admin' || role === 'employee' || role === 'customer' || role === 'global_support') return role;
        // For department-style values in uploaded sheets, default to employee.
        return role ? 'employee' : '';
      };

      const headers = parseCsvLine(lines[0]).map(normalizeHeader);
      const required = ['employee_id', 'name', 'email', 'role'];
      const missing = required.filter((h) => !headers.includes(h));
      if (missing.length > 0) {
        setBulkUsers([]);
        setBulkErrors([`Missing required column(s): ${missing.join(', ')}`]);
        return;
      }

      const col = {
        employee_id: headers.indexOf('employee_id'),
        name: headers.indexOf('name'),
        email: headers.indexOf('email'),
        role: headers.indexOf('role')
      };
      const parsedRows = [];
      const rowErrors = [];
      for (let i = 1; i < lines.length; i += 1) {
        const cells = parseCsvLine(lines[i]);
        const row = {
          employee_id: (cells[col.employee_id] || '').trim(),
          name: (cells[col.name] || '').trim(),
          email: (cells[col.email] || '').trim(),
          role: normalizeRole(cells[col.role])
        };
        if (!row.employee_id || !row.name || !row.email || !row.role) {
          rowErrors.push(`Row ${i + 1}: employee_id, name, email and role are required`);
          continue;
        }
        parsedRows.push(row);
      }
      setBulkUsers(parsedRows);
      setBulkErrors(rowErrors);
    } catch (error) {
      console.error('Bulk file parse error:', error);
      setBulkUsers([]);
      setBulkErrors(['Failed to read file']);
    }
  };

  const handleBulkSubmit = async () => {
    if (bulkUsers.length === 0) {
      toast.error('Upload a valid CSV with at least one user');
      return;
    }
    setSubmitting(true);
    try {
      const response = await bulkCreateUsers(bulkUsers);
      if (response?.success) {
        const summary = response.data || {};
        toast.success(`Bulk upload done: ${summary.createdCount || 0} created, ${summary.skippedCount || 0} skipped`);
        if (Array.isArray(summary.errors) && summary.errors.length > 0) {
          setBulkErrors(summary.errors.map((e) => `Row ${e.row}: ${e.message}`));
        } else {
          setBulkErrors([]);
        }
        setShowBulkModal(false);
        setBulkUsers([]);
        setBulkFileName('');
        loadUsers();
      } else {
        toast.error(response?.message || 'Bulk upload failed');
      }
    } catch (error) {
      console.error('Bulk upload error:', error);
      toast.error(error.response?.data?.message || 'Bulk upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const resetCreateForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'customer',
      is_active: true,
    });
    setTicketAccessTenantIds([]);
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleCreate = () => {
    resetCreateForm();
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    resetCreateForm();
  };

  const resetEditForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'customer',
      is_active: true,
    });
    setTicketAccessTenantIds([]);
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
    setSelectedUser(null);
  };

  const handleEdit = async (userRow) => {
    setSelectedUser(userRow);
    setFormData({
      name: userRow.name,
      email: userRow.email,
      password: '',
      confirmPassword: '',
      role: userRow.role,
      is_active: userRow.is_active,
    });
    setTicketAccessTenantIds([]);
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
    setShowEditModal(true);
    const r = String(userRow.role || '').toLowerCase();
    if (r === 'employee' || r === 'admin') {
      try {
        const res = await getUser(userRow.id);
        if (res.success && Array.isArray(res.data?.ticket_access_tenant_ids)) {
          setTicketAccessTenantIds(res.data.ticket_access_tenant_ids);
        }
      } catch (e) {
        console.error('getUser ticket access:', e);
      }
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    resetEditForm();
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedUser(null);
  };

  const validateForm = (isEdit = false) => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Password is optional for customers (passwordless), required for admins/employees
    // For create: only require password for admin/employee roles
    // For edit: password is optional (only validate if provided)
    if (isEdit) {
      // In edit mode, password is optional - only validate if provided
      if (formData.password) {
        if (formData.password.length < 6) {
          newErrors.password = 'Password must be at least 6 characters';
        }
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
      }
    } else {
      // In create mode: password required only for admin/employee, optional for customer
      if (formData.role !== 'customer') {
        if (!formData.password) {
          newErrors.password = 'Password is required for admin/employee accounts';
        } else if (formData.password.length < 6) {
          newErrors.password = 'Password must be at least 6 characters';
        }
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm(false)) {
      return;
    }

    setSubmitting(true);

    try {
      const userData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        role: formData.role,
      };

      // Only include password if provided (required for admin/employee, optional for customer)
      if (formData.password) {
        userData.password = formData.password;
      }

      if (formData.role === 'employee' || formData.role === 'admin') {
        userData.ticket_access_tenant_ids = ticketAccessTenantIds;
      }

      const response = await createUser(userData);

      if (response.success) {
        toast.success('User created successfully');
        handleCloseCreateModal();
        loadUsers();
      } else {
        toast.error(response.message || 'Failed to create user');
      }
    } catch (error) {
      console.error('Create user error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create user';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm(true)) {
      return;
    }

    setSubmitting(true);

    try {
      const userData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        role: formData.role,
        is_active: formData.is_active,
      };

      // Only include password if provided
      if (formData.password) {
        userData.password = formData.password;
      }

      if (formData.role === 'employee' || formData.role === 'admin') {
        userData.ticket_access_tenant_ids = ticketAccessTenantIds;
      }

      const response = await updateUser(selectedUser.id, userData);

      if (response.success) {
        toast.success('User updated successfully');
        handleCloseEditModal();
        loadUsers();
      } else {
        toast.error(response.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Update user error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update user';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    setSubmitting(true);

    try {
      const response = await deleteUser(selectedUser.id);

      if (response.success) {
        toast.success('User deleted successfully');
        handleCloseDeleteModal();
        loadUsers();
      } else {
        toast.error(response.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Delete user error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete user';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'role' && value === 'customer') {
      setTicketAccessTenantIds([]);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Premium Badge Components
  const RoleBadge = ({ role }) => {
    const roleConfig = {
      admin: {
        label: 'Admin',
        className: 'bg-gradient-to-r from-purple-500/15 to-purple-600/10 text-purple-700 dark:text-purple-300 border-purple-500/30 shadow-sm shadow-purple-500/20',
      },
      employee: {
        label: 'Employee',
        className: 'bg-gradient-to-r from-blue-500/15 to-indigo-600/10 text-blue-700 dark:text-blue-300 border-blue-500/30 shadow-sm shadow-blue-500/20',
      },
      customer: {
        label: 'Customer',
        className: 'bg-gradient-to-r from-green-500/15 to-emerald-600/10 text-green-700 dark:text-green-300 border-green-500/30 shadow-sm shadow-green-500/20',
      },
    };

    const config = roleConfig[role] || { label: role, className: 'bg-gray-500/15 text-gray-700 dark:text-gray-300 border-gray-500/30' };

    return (
      <span
        className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-semibold border backdrop-blur-sm transition-all duration-200 min-w-[96px] whitespace-nowrap ${config.className}`}
      >
        {config.label}
      </span>
    );
  };

  const isUserActive = (u) => u.is_active === true || u.is_active === 1 || String(u.is_active) === '1';
  const StatusBadge = ({ isActive }) => {
    const active = isActive === true || isActive === 1 || String(isActive) === '1';
    if (active) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold bg-gradient-to-r from-green-500/15 to-emerald-600/10 text-green-700 dark:text-green-300 border border-green-500/30 shadow-sm shadow-green-500/20 backdrop-blur-sm transition-all duration-200">
          <CheckCircle className="w-3.5 h-3.5" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold bg-gradient-to-r from-red-500/15 to-rose-600/10 text-red-700 dark:text-red-300 border border-red-500/30 shadow-sm shadow-red-500/20 backdrop-blur-sm transition-all duration-200">
        <XCircle className="w-3.5 h-3.5" />
        Inactive
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <DashboardLayout userRole={currentUser?.role}>
      <div className="flex flex-col overflow-hidden min-h-full">
        {/* Header – compact on mobile, fuller on desktop */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white/90 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-200/60 dark:border-gray-700/60 px-4 py-3 md:px-6 md:py-5 shadow-sm shadow-indigo-500/5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-xl hover:bg-indigo-50/60 dark:hover:bg-indigo-900/30 transition-all duration-200 hover:scale-105 hover:shadow-sm hover:shadow-indigo-500/30"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700 dark:text-gray-200" />
            </button>
            <div className="relative">
              <div className="pointer-events-none absolute -top-3 -left-4 h-10 w-32 rounded-full bg-indigo-500/10 blur-2xl" />
              <h1 className="text-xl md:text-2xl font-bold tracking-tight bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent">
                User Management
              </h1>
              <p className="mt-1 text-xs md:text-sm text-gray-600 dark:text-gray-400">
                Manage accounts and permissions
              </p>
            </div>
          </div>
          <div className="w-full md:w-auto flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => setShowBulkModal(true)}
              className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm
                bg-white/90 dark:bg-slate-800/80
                text-indigo-700 dark:text-indigo-300
                border border-indigo-200/80 dark:border-indigo-600/50
                shadow-sm
                hover:bg-white hover:-translate-y-0.5
                transition-all duration-300"
            >
              <Upload className="w-4 h-4 shrink-0" />
              <span>Bulk Upload</span>
            </button>
            <button
              onClick={handleCreate}
              className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm
                bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600
                text-white
                shadow-lg shadow-indigo-500/30
                hover:shadow-xl hover:shadow-indigo-500/40 hover:-translate-y-0.5 hover:scale-[1.02]
                active:scale-[0.98]
                transition-all duration-300"
            >
              <UserPlus className="w-4 h-4 shrink-0" />
              <span>Add User</span>
            </button>
          </div>
        </div>

        {/* Statistics Cards – allow horizontal scroll on mobile */}
        <div className="bg-white/50 dark:bg-slate-900/30 backdrop-blur-sm border-b border-gray-200/60 dark:border-gray-700/60 px-4 py-4 md:px-6 md:py-5">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 md:mb-4">Click a card to filter the table below.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 overflow-x-auto pb-2 md:pb-0">
            <div
              role="button"
              tabIndex={0}
              onClick={() => applyStatCardFilter('total')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); applyStatCardFilter('total'); } }}
              className={statCardClass(
                'bg-gradient-to-br from-white/80 to-blue-50/60 dark:from-slate-800/70 dark:to-blue-900/20 backdrop-blur-md rounded-xl p-4 border border-blue-200/60 dark:border-blue-800/30 shadow-sm shadow-blue-500/10',
                'total'
              )}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 font-medium uppercase tracking-wide">Total Users</p>
                  <p className="text-2xl font-bold text-blue-900 dark:text-blue-100 mt-1">{statistics.total}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-blue-500/15 dark:bg-blue-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => applyStatCardFilter('active')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); applyStatCardFilter('active'); } }}
              className={statCardClass(
                'bg-gradient-to-br from-white/80 to-green-50/60 dark:from-slate-800/70 dark:to-green-900/20 backdrop-blur-md rounded-xl p-4 border border-green-200/60 dark:border-green-800/30 shadow-sm shadow-green-500/10',
                'active'
              )}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-600 dark:text-green-400 font-medium uppercase tracking-wide">Active</p>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-100 mt-1">{statistics.active}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-green-500/15 dark:bg-green-500/20 flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => applyStatCardFilter('inactive')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); applyStatCardFilter('inactive'); } }}
              className={statCardClass(
                'bg-gradient-to-br from-white/80 to-red-50/60 dark:from-slate-800/70 dark:to-red-900/20 backdrop-blur-md rounded-xl p-4 border border-red-200/60 dark:border-red-800/30 shadow-sm shadow-red-500/10',
                'inactive'
              )}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-red-600 dark:text-red-400 font-medium uppercase tracking-wide">Inactive</p>
                  <p className="text-2xl font-bold text-red-900 dark:text-red-100 mt-1">{statistics.inactive}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-red-500/15 dark:bg-red-500/20 flex items-center justify-center">
                  <UserX className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
              </div>
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => applyStatCardFilter('admin')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); applyStatCardFilter('admin'); } }}
              className={statCardClass(
                'bg-gradient-to-br from-white/80 to-purple-50/60 dark:from-slate-800/70 dark:to-purple-900/20 backdrop-blur-md rounded-xl p-4 border border-purple-200/60 dark:border-purple-800/30 shadow-sm shadow-purple-500/10',
                'admin'
              )}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-purple-600 dark:text-purple-400 font-medium uppercase tracking-wide">Admins</p>
                  <p className="text-2xl font-bold text-purple-900 dark:text-purple-100 mt-1">{statistics.admins}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-purple-500/15 dark:bg-purple-500/20 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => applyStatCardFilter('customer')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); applyStatCardFilter('customer'); } }}
              className={statCardClass(
                'bg-gradient-to-br from-white/80 to-teal-50/60 dark:from-slate-800/70 dark:to-teal-900/20 backdrop-blur-md rounded-xl p-4 border border-teal-200/60 dark:border-teal-800/30 shadow-sm shadow-teal-500/10',
                'customer'
              )}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-teal-600 dark:text-teal-400 font-medium uppercase tracking-wide">Customers</p>
                  <p className="text-2xl font-bold text-teal-900 dark:text-teal-100 mt-1">{statistics.customers}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-teal-500/15 dark:bg-teal-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters – stacked on mobile */}
        <div className="bg-white/70 dark:bg-slate-900/50 backdrop-blur-sm border-b border-gray-200/60 dark:border-gray-700/60 px-4 py-3 md:px-6 md:py-4">
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1 relative">
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 uppercase tracking-wide">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 z-10" />
                <Input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border border-gray-300/60 dark:border-gray-600/60 !bg-white/80 dark:!bg-slate-800/60 !text-gray-900 dark:!text-gray-100 placeholder:!text-gray-500 dark:placeholder:!text-gray-400 focus:!bg-white dark:focus:!bg-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 rounded-xl transition-all duration-200"
                />
              </div>
            </div>
            <div className="w-full md:w-auto">
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 uppercase tracking-wide">Role</label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-4 py-2.5 border border-gray-300/60 dark:border-gray-600/60 bg-white/80 dark:bg-slate-800/60 text-gray-900 dark:text-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 min-w-[140px] transition-all duration-200"
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="customer">Customer</option>
              </select>
            </div>
            <div className="w-full md:w-auto">
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 uppercase tracking-wide">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2.5 border border-gray-300/60 dark:border-gray-600/60 bg-white/80 dark:bg-slate-800/60 text-gray-900 dark:text-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 min-w-[140px] transition-all duration-200"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <button
              onClick={exportToCSV}
              className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm
                bg-gradient-to-r from-indigo-500/10 via-blue-500/10 to-purple-500/10
                text-indigo-700 dark:text-indigo-300
                border border-indigo-500/30 dark:border-indigo-500/20
                shadow-sm shadow-indigo-500/10
                hover:shadow-lg hover:shadow-indigo-500/25 hover:-translate-y-0.5
                active:scale-[0.98]
                transition-all duration-300
                backdrop-blur-sm"
              title="Export to CSV"
            >
              <Download className="w-4 h-4 shrink-0" />
              <span className="leading-none">Export CSV</span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-50/50 via-white/30 to-indigo-50/30 dark:from-slate-900/50 dark:via-slate-800/30 dark:to-indigo-900/20 p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin shadow-lg shadow-indigo-500/20" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-16 bg-white/80 dark:bg-slate-800/60 backdrop-blur-md rounded-xl border border-gray-200/60 dark:border-gray-700/60 shadow-lg shadow-indigo-500/5">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-indigo-500/10 dark:bg-indigo-500/20 flex items-center justify-center">
                <Users className="w-8 h-8 text-indigo-500 dark:text-indigo-400" />
              </div>
              <p className="text-gray-700 dark:text-gray-300 font-semibold mb-2">No users found</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {searchTerm || roleFilter !== 'all' || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Create your first user to get started'}
              </p>
            </div>
          ) : (
            <div className="bg-white/80 dark:bg-slate-800/60 backdrop-blur-md rounded-xl border border-gray-200/60 dark:border-gray-700/60 shadow-lg shadow-indigo-500/10 overflow-hidden">
              {/* Desktop table view */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 border-b border-gray-200/60 dark:border-gray-700/60">
                    <tr>
                      <th 
                        className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-indigo-500/20 dark:hover:bg-indigo-500/10 transition-all duration-200"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center gap-2">
                          User
                          {sortBy === 'name' && (
                            sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-indigo-500/20 dark:hover:bg-indigo-500/10 transition-all duration-200"
                        onClick={() => handleSort('role')}
                      >
                        <div className="flex items-center gap-2">
                          Role
                          {sortBy === 'role' && (
                            sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-indigo-500/20 dark:hover:bg-indigo-500/10 transition-all duration-200"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center gap-2">
                          Status
                          {sortBy === 'status' && (
                            sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-indigo-500/20 dark:hover:bg-indigo-500/10 transition-all duration-200"
                        onClick={() => handleSort('created')}
                      >
                        <div className="flex items-center gap-2">
                          Created
                          {sortBy === 'created' && (
                            sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200/60 dark:divide-gray-700/60 bg-white/50 dark:bg-slate-800/30">
                    {paginatedUsers.map((user) => (
                      <tr 
                        key={user.id} 
                        className="hover:bg-gradient-to-r hover:from-indigo-50/60 hover:via-purple-50/40 hover:to-transparent dark:hover:from-indigo-900/20 dark:hover:via-purple-900/10 dark:hover:to-transparent transition-all duration-200 cursor-pointer hover:shadow-lg hover:shadow-indigo-500/5 hover:-translate-y-[1px] group"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-11 h-11 bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md shadow-indigo-500/30">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{user.name.replace(/0+$/, '').trim()}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mt-0.5">
                                <Mail className="w-3 h-3" />
                                {user.email}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mt-0.5">
                                <Phone className="w-3 h-3" />
                                {user.mobile_phone || user.phone_number || 'N/A'}
                              </p>
                              <p className="text-[11px] text-indigo-600 dark:text-indigo-400 font-semibold mt-1">
                                Employee Code: {user.employee_code || user.employee_id || 'N/A'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <RoleBadge role={user.role} />
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge isActive={user.is_active} />
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                            {formatDate(user.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEdit(user)}
                              className="p-2 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/15 dark:hover:bg-indigo-500/20 rounded-lg transition-all duration-200 hover:scale-110 hover:shadow-sm hover:shadow-indigo-500/20"
                              title="Edit user"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            {user.id !== currentUser?.id && (
                              <button
                                onClick={() => handleDelete(user)}
                                className="p-2 text-red-600 dark:text-red-400 hover:bg-red-500/15 dark:hover:bg-red-500/20 rounded-lg transition-all duration-200 hover:scale-110 hover:shadow-sm hover:shadow-red-500/20"
                                title="Delete user"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile card view */}
              <div className="md:hidden divide-y divide-gray-200/60 dark:divide-gray-700/60">
                {paginatedUsers.map((user) => (
                  <div key={user.id} className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md shadow-indigo-500/30">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 line-clamp-1">
                            {user.name.replace(/0+$/, '').trim()}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mt-0.5">
                            <Mail className="w-3 h-3" />
                            <span className="truncate max-w-[160px]">{user.email}</span>
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mt-0.5">
                            <Phone className="w-3 h-3" />
                            <span className="truncate max-w-[160px]">{user.mobile_phone || user.phone_number || 'N/A'}</span>
                          </p>
                          <p className="text-[11px] text-indigo-600 dark:text-indigo-400 font-semibold mt-1">
                            Emp Code: {user.employee_code || user.employee_id || 'N/A'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleEdit(user)}
                        className="p-2 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-slate-700 transition-colors"
                        aria-label="Edit user"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap items-center gap-2">
                        <RoleBadge role={user.role} />
                        <StatusBadge isActive={user.is_active} />
                      </div>
                      <span className="text-[11px] text-gray-500 dark:text-gray-400">
                        {formatDate(user.created_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {filteredUsers.length > 0 && (
                <div className="bg-gradient-to-r from-gray-50/80 to-indigo-50/40 dark:from-slate-800/60 dark:to-indigo-900/20 backdrop-blur-sm px-6 py-4 border-t border-gray-200/60 dark:border-gray-700/60 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                    Showing <span className="font-bold text-indigo-600 dark:text-indigo-400">{(safeCurrentPage - 1) * USERS_PAGE_SIZE + 1}</span>-
                    <span className="font-bold text-indigo-600 dark:text-indigo-400">{Math.min(safeCurrentPage * USERS_PAGE_SIZE, filteredUsers.length)}</span> of{' '}
                    <span className="font-bold text-indigo-600 dark:text-indigo-400">{filteredUsers.length}</span> filtered
                    (total <span className="font-bold text-indigo-600 dark:text-indigo-400">{users.length}</span>)
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={safeCurrentPage === 1}
                        className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-300/70 dark:border-gray-600/70 bg-white/70 dark:bg-slate-800/70 text-gray-700 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-xs md:text-sm text-gray-600 dark:text-gray-400 px-2">
                        Page {safeCurrentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={safeCurrentPage === totalPages}
                        className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-300/70 dark:border-gray-600/70 bg-white/70 dark:bg-slate-800/70 text-gray-700 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                        aria-label="Next page"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                    {(searchTerm || roleFilter !== 'all' || statusFilter !== 'all') && (
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          setRoleFilter('all');
                          setStatusFilter('all');
                          setCurrentPage(1);
                        }}
                        className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-semibold flex items-center gap-1.5 hover:gap-2 transition-all duration-200"
                      >
                        <X className="w-4 h-4" />
                        Clear Filters
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bulk Upload Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 dark:bg-slate-800/95 rounded-2xl w-full max-w-2xl border border-gray-200/60 dark:border-gray-700/60 shadow-2xl">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Bulk Upload Users
                </h3>
                <button
                  onClick={() => {
                    setShowBulkModal(false);
                    setBulkFileName('');
                    setBulkUsers([]);
                    setBulkErrors([]);
                  }}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-all duration-200 hover:scale-110"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="rounded-xl border border-indigo-200/70 dark:border-indigo-700/50 bg-indigo-50/60 dark:bg-indigo-900/20 p-4">
                <p className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">Required CSV columns</p>
                <p className="mt-1 text-xs text-indigo-700/90 dark:text-indigo-300/90">
                  employee_id, name, email, role (role: admin / employee / customer). Phone number can be updated later by each user.
                </p>
                <button
                  type="button"
                  onClick={downloadBulkTemplate}
                  className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-white dark:bg-slate-800 border border-indigo-300/70 dark:border-indigo-600/50 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-slate-700 transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download Template
                </button>
              </div>

              <div>
                <Label htmlFor="bulk-users-file" className="text-sm font-medium text-gray-700 dark:text-gray-300">Upload CSV file</Label>
                <input
                  id="bulk-users-file"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleBulkFileChange}
                  className="mt-2 block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-4 file:rounded-lg file:border-0 file:bg-indigo-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-indigo-700"
                />
                {bulkFileName && (
                  <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">Selected: {bulkFileName}</p>
                )}
              </div>

              <div className="rounded-xl border border-gray-200/70 dark:border-gray-700/60 bg-white/80 dark:bg-slate-900/40 p-4">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  Ready to upload: <span className="text-indigo-600 dark:text-indigo-400">{bulkUsers.length}</span> users
                </p>
                {bulkErrors.length > 0 && (
                  <div className="mt-2 max-h-36 overflow-auto rounded-lg border border-red-200 dark:border-red-800 bg-red-50/60 dark:bg-red-900/20 p-2">
                    {bulkErrors.slice(0, 20).map((err, idx) => (
                      <p key={`${err}-${idx}`} className="text-xs text-red-700 dark:text-red-300">{err}</p>
                    ))}
                    {bulkErrors.length > 20 && (
                      <p className="text-xs text-red-700 dark:text-red-300 mt-1">...and {bulkErrors.length - 20} more</p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkModal(false);
                    setBulkFileName('');
                    setBulkUsers([]);
                    setBulkErrors([]);
                  }}
                  className="px-4 py-2.5 rounded-xl font-semibold text-sm bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleBulkSubmit}
                  disabled={submitting || bulkUsers.length === 0}
                  className="px-4 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Uploading...' : 'Upload Users'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-md rounded-2xl w-full max-w-md border border-gray-200/60 dark:border-gray-700/60 shadow-2xl shadow-indigo-500/20">
            {/* Gradient Header */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Create New User
                </h3>
                <button
                  onClick={handleCloseCreateModal}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-all duration-200 hover:scale-110"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
            
            <div className="p-6">

            <form onSubmit={handleCreateSubmit}>
              <div className="mb-5">
                <Label htmlFor="create-name" className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <UserPlus className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  Full Name
                </Label>
                <Input
                  id="create-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  className={`border ${errors.name ? 'border-red-500/60 focus:border-red-500 focus:ring-red-500/30 focus:shadow-lg focus:shadow-red-500/20' : 'border-gray-300/60 dark:border-gray-600/60 focus:border-indigo-500 focus:ring-indigo-500/30 focus:shadow-lg focus:shadow-indigo-500/20'} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all duration-200 bg-white/80 dark:bg-slate-700/60 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400`}
                  placeholder="Enter full name"
                />
                {errors.name && (
                  <p className="text-xs text-red-600 mt-1.5">{errors.name}</p>
                )}
              </div>

              <div className="mb-5">
                <Label htmlFor="create-email" className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Mail className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  Email Address
                </Label>
                <Input
                  id="create-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`border ${errors.email ? 'border-red-500/60 focus:border-red-500 focus:ring-red-500/30 focus:shadow-lg focus:shadow-red-500/20' : 'border-gray-300/60 dark:border-gray-600/60 focus:border-indigo-500 focus:ring-indigo-500/30 focus:shadow-lg focus:shadow-indigo-500/20'} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all duration-200 bg-white/80 dark:bg-slate-700/60 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400`}
                  placeholder="Enter email address"
                />
                {errors.email && (
                  <p className="text-xs text-red-600 mt-1.5">{errors.email}</p>
                )}
              </div>

              <div className="mb-5">
                <Label htmlFor="create-role" className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Shield className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  Role
                </Label>
                <select
                  id="create-role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300/60 dark:border-gray-600/60 rounded-xl bg-white/80 dark:bg-slate-700/60 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 focus:shadow-lg focus:shadow-indigo-500/20 transition-all duration-200"
                >
                  <option value="customer">Customer</option>
                  <option value="employee">Employee</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {(formData.role === 'admin' || formData.role === 'employee') &&
                tenantAccessOptions.filter((t) => t.id !== currentUser?.tenant_id).length > 0 && (
                <div className="mb-5 p-3 rounded-xl border border-indigo-200/60 dark:border-indigo-800/40 bg-indigo-50/40 dark:bg-indigo-950/20">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                    Cross-tenant ticket assignment
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Home tenant is always included. Select other tenants this agent may be assigned tickets from.
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-2">
                    {tenantAccessOptions
                      .filter((t) => t.id !== currentUser?.tenant_id)
                      .map((t) => (
                        <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-indigo-600"
                            checked={ticketAccessTenantIds.includes(t.id)}
                            onChange={() => {
                              setTicketAccessTenantIds((prev) =>
                                prev.includes(t.id)
                                  ? prev.filter((x) => x !== t.id)
                                  : [...prev, t.id]
                              );
                            }}
                          />
                          <span>{t.name}</span>
                        </label>
                      ))}
                  </div>
                </div>
              )}

              {/* Password fields - only show for admin/employee */}
              {(formData.role === 'admin' || formData.role === 'employee') && (
                <>
                  <div className="mb-5">
                    <Label htmlFor="create-password" className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                      <Lock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      Password <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="create-password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleChange}
                        className={`border pr-10 ${errors.password ? 'border-red-500/60 focus:border-red-500 focus:ring-red-500/30 focus:shadow-lg focus:shadow-red-500/20' : 'border-gray-300/60 dark:border-gray-600/60 focus:border-indigo-500 focus:ring-indigo-500/30 focus:shadow-lg focus:shadow-indigo-500/20'} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all duration-200 bg-white/80 dark:bg-slate-700/60 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400`}
                        placeholder="Enter password (min 6 characters)"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-xs text-red-600 mt-1.5">{errors.password}</p>
                    )}
                  </div>

                  <div className="mb-5">
                    <Label htmlFor="create-confirm-password" className="mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                      Confirm Password <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="create-confirm-password"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className={`border pr-10 ${errors.confirmPassword ? 'border-red-500/60 focus:border-red-500 focus:ring-red-500/30 focus:shadow-lg focus:shadow-red-500/20' : 'border-gray-300/60 dark:border-gray-600/60 focus:border-indigo-500 focus:ring-indigo-500/30 focus:shadow-lg focus:shadow-indigo-500/20'} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all duration-200 bg-white/80 dark:bg-slate-700/60 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400`}
                        placeholder="Confirm password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-xs text-red-600 mt-1.5">{errors.confirmPassword}</p>
                    )}
                  </div>
                </>
              )}

              {/* Info message for customers */}
              {formData.role === 'customer' && (
                <div className="mb-5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-3">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    <strong>Passwordless Registration:</strong> Customer accounts use email-based OTP login. No password required.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200/60 dark:border-gray-700/60">
                <button
                  type="button"
                  onClick={handleCloseCreateModal}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm
                    bg-gradient-to-r from-gray-500/10 to-gray-600/10
                    text-gray-700 dark:text-gray-300
                    border border-gray-300/60 dark:border-gray-600/60
                    hover:shadow-lg hover:shadow-gray-500/20 hover:-translate-y-0.5
                    active:scale-[0.98]
                    transition-all duration-300
                    backdrop-blur-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap
                    bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600
                    text-white
                    shadow-lg shadow-indigo-500/30
                    hover:shadow-xl hover:shadow-indigo-500/40 hover:-translate-y-0.5 hover:scale-[1.02]
                    active:scale-[0.98]
                    transition-all duration-300
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:translate-y-0"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
                      <span className="leading-none">Creating...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 shrink-0" />
                      <span className="leading-none">Create User</span>
                    </>
                  )}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 backdrop-blur-md rounded-2xl w-full max-w-md border-2 border-gray-300 dark:border-gray-700 shadow-2xl shadow-gray-900/50 dark:shadow-indigo-500/20">
            {/* Gradient Header */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Edit2 className="w-5 h-5" />
                  Edit User
                </h3>
                <button
                  onClick={handleCloseEditModal}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-all duration-200 hover:scale-110"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
            
            <div className="p-6">

            <form onSubmit={handleEditSubmit}>
              <div className="mb-5">
                <Label htmlFor="edit-name" className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <UserPlus className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  Full Name
                </Label>
                <Input
                  id="edit-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  className={`border ${errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30 focus:shadow-lg focus:shadow-red-500/20' : 'border-gray-300 dark:border-gray-600 focus:border-indigo-500 focus:ring-indigo-500/30 focus:shadow-lg focus:shadow-indigo-500/20'} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all duration-200 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400`}
                  placeholder="Enter full name"
                />
                {errors.name && (
                  <p className="text-xs text-red-600 mt-1.5">{errors.name}</p>
                )}
              </div>

              <div className="mb-5">
                <Label htmlFor="edit-email" className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Mail className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  Email Address
                </Label>
                <Input
                  id="edit-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`border ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30 focus:shadow-lg focus:shadow-red-500/20' : 'border-gray-300 dark:border-gray-600 focus:border-indigo-500 focus:ring-indigo-500/30 focus:shadow-lg focus:shadow-indigo-500/20'} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all duration-200 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400`}
                  placeholder="Enter email address"
                />
                {errors.email && (
                  <p className="text-xs text-red-600 mt-1.5">{errors.email}</p>
                )}
              </div>

              <div className="mb-5">
                <Label htmlFor="edit-role" className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Shield className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  Role
                </Label>
                <select
                  id="edit-role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-colors disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:text-gray-500 dark:disabled:text-gray-400 disabled:cursor-not-allowed"
                  disabled={selectedUser.id === currentUser?.id && formData.role === 'admin'}
                >
                  <option value="customer">Customer</option>
                  <option value="employee">Employee</option>
                  <option value="admin">Admin</option>
                </select>
                {selectedUser.id === currentUser?.id && formData.role === 'admin' && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                    You cannot change your own role from admin
                  </p>
                )}
              </div>

              {(formData.role === 'admin' || formData.role === 'employee') &&
                selectedUser &&
                tenantAccessOptions.filter((t) => t.id !== selectedUser.tenant_id).length > 0 && (
                <div className="mb-5 p-3 rounded-xl border border-indigo-200/60 dark:border-indigo-800/40 bg-indigo-50/40 dark:bg-indigo-950/20">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                    Cross-tenant ticket assignment
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Home tenant is always included. Select other tenants this agent may be assigned tickets from.
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-2">
                    {tenantAccessOptions
                      .filter((t) => t.id !== selectedUser.tenant_id)
                      .map((t) => (
                        <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-indigo-600"
                            checked={ticketAccessTenantIds.includes(t.id)}
                            onChange={() => {
                              setTicketAccessTenantIds((prev) =>
                                prev.includes(t.id)
                                  ? prev.filter((x) => x !== t.id)
                                  : [...prev, t.id]
                              );
                            }}
                          />
                          <span>{t.name}</span>
                        </label>
                      ))}
                  </div>
                </div>
              )}

              <div className="mb-5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="w-4 h-4 border-2 border-gray-300 dark:border-gray-600 rounded text-indigo-600 focus:ring-2 focus:ring-indigo-500/30 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-slate-700"
                    disabled={selectedUser.id === currentUser?.id}
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active Account</span>
                </label>
                {selectedUser.id === currentUser?.id && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                    You cannot deactivate your own account
                  </p>
                )}
              </div>

              <div className="mb-5 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                  Leave password fields empty to keep current password
                </p>
                <div className="mb-5">
                  <Label htmlFor="edit-password" className="mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">New Password (Optional)</Label>
                  <div className="relative">
                    <Input
                      id="edit-password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      className={`border pr-10 ${errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30 focus:shadow-lg focus:shadow-red-500/20' : 'border-gray-300 dark:border-gray-600 focus:border-indigo-500 focus:ring-indigo-500/30 focus:shadow-lg focus:shadow-indigo-500/20'} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all duration-200 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400`}
                      placeholder="Enter new password (min 6 characters)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-xs text-red-600 mt-1.5">{errors.password}</p>
                  )}
                </div>

                <div className="mb-4">
                  <Label htmlFor="edit-confirm-password" className="mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Confirm New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="edit-confirm-password"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`border pr-10 ${errors.confirmPassword ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30 focus:shadow-lg focus:shadow-red-500/20' : 'border-gray-300 dark:border-gray-600 focus:border-indigo-500 focus:ring-indigo-500/30 focus:shadow-lg focus:shadow-indigo-500/20'} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all duration-200 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400`}
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-xs text-red-600 mt-1.5">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200/60 dark:border-gray-700/60">
                <button
                  type="button"
                  onClick={handleCloseEditModal}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm
                    bg-gradient-to-r from-gray-500/10 to-gray-600/10
                    text-gray-700 dark:text-gray-300
                    border border-gray-300/60 dark:border-gray-600/60
                    hover:shadow-lg hover:shadow-gray-500/20 hover:-translate-y-0.5
                    active:scale-[0.98]
                    transition-all duration-300
                    backdrop-blur-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap
                    bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600
                    text-white
                    shadow-lg shadow-indigo-500/30
                    hover:shadow-xl hover:shadow-indigo-500/40 hover:-translate-y-0.5 hover:scale-[1.02]
                    active:scale-[0.98]
                    transition-all duration-300
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:translate-y-0"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
                      <span className="leading-none">Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 shrink-0" />
                      <span className="leading-none">Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-md rounded-2xl w-full max-w-md border border-red-200/60 dark:border-red-800/60 shadow-2xl shadow-red-500/20">
            {/* Gradient Header */}
            <div className="bg-gradient-to-r from-red-600 via-rose-600 to-pink-600 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Trash2 className="w-5 h-5" />
                  Delete User
                </h3>
                <button
                  onClick={handleCloseDeleteModal}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-all duration-200 hover:scale-110"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 font-medium">
                  Are you sure you want to delete this user?
                </p>
                <div className="bg-gradient-to-br from-gray-50/80 to-red-50/40 dark:from-slate-700/60 dark:to-red-900/20 backdrop-blur-sm p-4 rounded-xl border border-gray-200/60 dark:border-gray-700/60">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{selectedUser.name}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{selectedUser.email}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <RoleBadge role={selectedUser.role} />
                    <StatusBadge isActive={selectedUser.is_active} />
                  </div>
                </div>
                <p className="text-xs text-red-600 dark:text-red-400 mt-4 font-medium bg-red-50/50 dark:bg-red-900/20 px-3 py-2 rounded-lg border border-red-200/60 dark:border-red-800/60">
                  ⚠️ This action cannot be undone. The user will be permanently deleted.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200/60 dark:border-gray-700/60">
                <button
                  type="button"
                  onClick={handleCloseDeleteModal}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm
                    bg-gradient-to-r from-gray-500/10 to-gray-600/10
                    text-gray-700 dark:text-gray-300
                    border border-gray-300/60 dark:border-gray-600/60
                    hover:shadow-lg hover:shadow-gray-500/20 hover:-translate-y-0.5
                    active:scale-[0.98]
                    transition-all duration-300
                    backdrop-blur-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap
                    bg-gradient-to-r from-red-600 via-rose-600 to-pink-600
                    text-white
                    shadow-lg shadow-red-500/30
                    hover:shadow-xl hover:shadow-red-500/40 hover:-translate-y-0.5 hover:scale-[1.02]
                    active:scale-[0.98]
                    transition-all duration-300
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:translate-y-0"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
                      <span className="leading-none">Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 shrink-0" />
                      <span className="leading-none">Delete User</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </DashboardLayout>
  );
};

export default UserManagement;
